<template>
  <div class="action-plan-timeline">
    <div class="timeline-container">
      <!-- 标题区域 -->
      <div class="timeline-title" :class="{ 'title-active': currentStage === 0 }">
        全域产改三年行动
      </div>
      
      <!-- 进度条和阶段 -->
      <div class="timeline-progress">
        <!-- 进度条背景 -->
        <div class="progress-bar-bg"></div>
        
        <!-- 进度条填充 -->
        <div class="progress-bar-fill" :style="{ width: progressWidth + '%' }"></div>
        
        <!-- 阶段标记 -->
        <div class="stages">
          <div
            v-for="(stage, index) in stages"
            :key="index"
            class="stage-item"
            :class="{
              'stage-active': currentStage === index,
              'stage-completed': currentStage > index,
              'stage-pending': currentStage < index
            }"
          >
            <!-- 阶段标签 -->
            <div class="stage-label">{{ stage.label }}</div>
            
            <!-- 圆形标记 -->
            <div class="stage-marker">
              <div class="marker-dot"></div>
              <div class="marker-ring" v-if="currentStage === index"></div>
            </div>
            
            <!-- 年份 -->
            <div class="stage-year" :class="{ 'year-active': currentStage === index }">
              {{ stage.year }}年
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ActionPlanTimeline',
  data() {
    return {
      currentStage: -1, // 当前激活的阶段索引：-1表示还未开始
      progressWidth: 0, // 进度条宽度百分比
      stages: [
        { label: '启动阶段', year: 2025 },
        { label: '提升阶段', year: 2026 },
        { label: '巩固阶段', year: 2027 },
      ],
      // 每个阶段的动画持续时间（毫秒）- 调慢速度
      stageDuration: 4000,
      // 到达阶段后的停留时间（毫秒）
      stageStayDuration: 2000,
    }
  },
  mounted() {
    this.startProgress()
  },
  beforeDestroy() {
    this.stopProgress()
  },
  methods: {
    startProgress() {
      // 从第一个阶段开始
      this.animateToNextStage(0)
    },
    
    animateToNextStage(stageIndex) {
      // 如果超出阶段数量，从头开始循环
      if (stageIndex >= this.stages.length) {
        // 重置进度条和阶段
        this.currentStage = -1
        this.progressWidth = 0
        // 等待更长时间后重新开始，让用户有时间欣赏完整效果
        setTimeout(() => {
          this.animateToNextStage(0)
        }, 2000)
        return
      }
      
      // 计算目标进度条宽度
      const totalStages = this.stages.length
      let targetProgress
      if (totalStages === 1) {
        targetProgress = 100
      } else {
        // 进度条到达每个阶段标记点的位置
        // 如果有3个阶段，标记点分别在 0%, 50%, 100% 位置
        targetProgress = (stageIndex / (totalStages - 1)) * 100
      }
      
      // 确保进度条不超过100%
      targetProgress = Math.min(targetProgress, 100)
      
      // 动画进度条到当前阶段
      this.animateProgressBar(targetProgress, () => {
        // 进度条到达后，激活当前阶段（放大效果）
        this.currentStage = stageIndex
        
        // 如果是最后一个阶段，停留更长时间后再循环
        const stayTime = stageIndex === this.stages.length - 1 
          ? this.stageStayDuration * 2 
          : this.stageStayDuration
        
        // 停留一段时间后，继续下一个阶段
        setTimeout(() => {
          this.animateToNextStage(stageIndex + 1)
        }, stayTime)
      })
    },
    
    animateProgressBar(targetWidth, onComplete) {
      const startWidth = this.progressWidth
      const duration = this.stageDuration
      const startTime = Date.now()
      
      const animate = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        // 使用缓动函数
        const easeProgress = this.easeInOutCubic(progress)
        this.progressWidth = startWidth + (targetWidth - startWidth) * easeProgress
        
        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(animate)
        } else {
          this.progressWidth = targetWidth
          if (onComplete) {
            onComplete()
          }
        }
      }
      
      // 取消之前的动画
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame)
      }
      
      this.animationFrame = requestAnimationFrame(animate)
    },
    
    easeInOutCubic(t) {
      // 使用更柔和的缓动函数，让过渡更平滑
      return t < 0.5
        ? 2 * t * t
        : 1 - Math.pow(-2 * t + 2, 2) / 2
    },
    
    stopProgress() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame)
        this.animationFrame = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.action-plan-timeline {
  width: 100%;
  padding: 0;
  position: relative;
  z-index: 10;
  flex-shrink: 0;
}

.timeline-container {
  position: relative;
  width: 100%;
  height: 140px;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(0, 30, 60, 0.6) 0%, rgba(7, 20, 36, 0.4) 100%);
  border: 1px solid rgba(0, 217, 255, 0.3);
  border-radius: 12px;
  padding: 0 30px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

// 标题区域
.timeline-title {
  position: absolute;
  left: 30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 30px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(0, 217, 255, 0.2) 0%, rgba(0, 217, 255, 0.1) 100%);
  border: 1px solid rgba(0, 217, 255, 0.3);
  border-radius: 8px;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 3;
  
  &.title-active {
    background: linear-gradient(135deg, rgba(0, 217, 255, 0.4) 0%, rgba(0, 217, 255, 0.2) 100%);
    border-color: rgba(0, 217, 255, 0.6);
    box-shadow: 0 0 20px rgba(0, 217, 255, 0.4);
    transform: translateY(-50%) scale(1.05);
  }
}

// 进度条区域
.timeline-progress {
  position: relative;
  width: 100%;
  height: 60px;
  margin-left: 280px; // 为标题留出空间
  display: flex;
  align-items: center;
}

// 进度条背景
.progress-bar-bg {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  z-index: 1;
}

// 进度条填充
.progress-bar-fill {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 8px;
  background: linear-gradient(90deg, #1487ff 0%, #1487ff 50%, #1487ff 100%);
  border-radius: 4px;
  box-shadow: 0 0 15px rgba(20, 135, 255, 0.8), inset 0 0 10px rgba(20, 135, 255, 0.3);
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    width: 20px;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(20, 135, 255, 0.5) 100%);
    border-radius: 0 4px 4px 0;
  }
}

// 阶段容器
.stages {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 3;
}

// 阶段项
.stage-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

// 阶段标签
.stage-label {
  font-size: 24px;
  font-weight: 500;
  color: #9ab3c0;
  white-space: nowrap;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  
  .stage-active & {
    color: #00d9ff;
    font-weight: 600;
    text-shadow: 0 0 10px rgba(0, 217, 255, 0.5);
  }
  
  .stage-completed & {
    color: #00d9ff;
  }
}

// 圆形标记
.stage-marker {
  position: relative;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.marker-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(107, 125, 143, 0.5);
  border: 2px solid rgba(154, 179, 192, 0.6);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  
  .stage-active & {
    width: 22px;
    height: 22px;
    background: #1487ff;
    border-color: #1487ff;
    box-shadow: 0 0 25px rgba(0, 217, 255, 1), 0 0 40px rgba(0, 234, 255, 0.6);
    animation: dotPulse 2s infinite;
  }
  
  .stage-completed & {
    width: 18px;
    height: 18px;
    background: #1487ff;
    border-color: #1487ff;
    box-shadow: 0 0 15px rgba(0, 217, 255, 0.7);
  }
}

@keyframes dotPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.9;
  }
}

.marker-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  border: 2px solid rgba(0, 217, 255, 0.6);
  border-radius: 50%;
  animation: markerPulse 2s infinite;
}

@keyframes markerPulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

// 年份
.stage-year {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 4px;
  
  &.year-active {
    font-size: 32px;
    font-weight: 800;
    color: #00d9ff;
    text-shadow: 0 0 20px rgba(0, 217, 255, 0.8), 0 0 30px rgba(0, 234, 255, 0.4);
    transform: scale(1.15);
    letter-spacing: 1px;
    animation: yearGlow 2s infinite;
  }
}

@keyframes yearGlow {
  0%, 100% {
    text-shadow: 0 0 20px rgba(0, 217, 255, 0.8), 0 0 30px rgba(0, 234, 255, 0.4);
  }
  50% {
    text-shadow: 0 0 30px rgba(0, 217, 255, 1), 0 0 50px rgba(0, 234, 255, 0.6);
  }
}

// 响应式调整
@media (max-width: 1600px) {
  .timeline-title {
    font-size: 20px;
    padding: 10px 20px;
  }
  
  .timeline-progress {
    margin-left: 240px;
  }
  
  .stage-label {
    font-size: 20px;
  }
  
  .stage-year {
    font-size: 20px;
    
    &.year-active {
      font-size: 24px;
    }
  }
}
</style>
